import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import React, {useRef, useState} from 'react';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
// @ts-expect-error: No declaration file for module
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import FunctionDropdown from 'notebook/tables/core-ui/FunctionDropdown';
import FunctionHelpDropdown from 'notebook/tables/core-ui/FunctionHelpDropdown';
import {SpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import tokenizer from 'notebook/tables/excess/Tokenizer';
import {AnalysisFormulaParsingError, COLUMN_REFERENCE_PREFIX} from 'pipelines/formula/Constants';
import formulaTextToFormulaAstJson from 'pipelines/formula/formulaTextToFormulaAstJson';
import {
  formulaTextToHTML,
  getFormulaLength,
  htmlToFormulaText,
  getScopedFunction,
  partitionFormulaText,
} from 'pipelines/formula/formulaUtil';
import {analysisFunctions} from 'pipelines/formula/functionUtils';
import {
  getDataFrameInfoOrEmptyInfo,
  useStepConfigEditorContext,
} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import AttachDropdown from 'ui-library/attach-dropdown/AttachDropdown';
import {BLUE_LIGHTER, GRAY, GRAY_DARKER} from 'ui-library/Colors';
import ClickableIcon, {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {isEnterKey} from 'util/Keyboard';
import {ElementClipboardEvent, ElementKeyboardEvent, JSONValue} from 'util/Types';

import AnalysisFormulaSearchDropdown from './AnalysisFormulaSearchDropdown';

type Formula = PipelineStepConfig<PipelineStepType.COMPUTED_COLUMN, 0, 'draft'>['formula'];

interface Selection {
  start: number;
  end: number;
}

/**
 * If selection is null, then infer that the action will just be performed at the very end of the formula.
 */
function resolveSelection(formulaText: string, selection: Selection | null): Selection {
  const formulaEnd = getFormulaLength(formulaText);
  return selection ?? {start: formulaEnd, end: formulaEnd};
}

export type AnalysisFormulaEditorProps = CustomComponentEditorProps;

function computeFormulaAst(formulaText: string | null): JSONValue {
  if (formulaText == null) {
    return null;
  }
  try {
    return formulaTextToFormulaAstJson(formulaText);
  } catch (e) {
    if (e instanceof AnalysisFormulaParsingError) {
      return null;
    }
    throw e;
  }
}

export default function AnalysisFormulaEditor({
  config: fullConfig,
  onChangeConfig,
  disabled,
}: AnalysisFormulaEditorProps): JSX.Element {
  const formula = fullConfig.formula as Formula;
  const formulaText = formula.formulaText ?? '';

  function handleChangeFormula(newFormula: Formula): void {
    const formulaWithAst = {...newFormula, formulaAst: computeFormulaAst(newFormula.formulaText)};
    onChangeConfig(fullConfig, ['formula'], formulaWithAst as JSONValue);
  }
  const {dataFramesInfos} = useStepConfigEditorContext();
  const {dataFrameSpec} = getDataFrameInfoOrEmptyInfo(dataFramesInfos, SINGLE_DATASET_INPUT_STEP_DATA_NAME);
  const [selection, setSelection] = useState<Selection | null>(null);

  const [functionDropdownOpen, setFunctionDropdownOpen] = useState(false);
  const [canDisplayEditorDropdown, setCanDisplayEditorDropdown] = useState(false);
  const formulaSearchDropdownRef = useRef<AttachDropdown>(null);

  // Divs with contenteditable=true need to be uncontrolled so that the cursor position stays in sync, so we
  // only use the initial value of the prop
  const [initialInputValue] = useState(formulaText);
  const editableDivRef = useRef<HTMLDivElement>(null);

  const tokens = tokenizer.partialTokenize(formulaText, {relative: false});
  let formulaSearchQuery: string | null = null;

  // These will only be non-null when the user's cursor is in the middle of the formula text
  // queryStart and queryEnd are in terms of characters, not tokens (i.e., `$c$x` is length 4, not 1)
  let queryStart: number | null = null;
  let queryEnd: number | null = null;

  // Define the formula search query if the cursor is at a single position. The search query is defined
  // as follows:
  // - if the cursor is at the end of the formula text, then the search query is `tokens.leftover`
  //   (all the trailing un-tokenized characters in the formula text)
  // - if the cursor is in the middle of the formula text and `tokens.leftover` exists, then the
  //   search query is the substring from the beginning of `tokens.leftover` up to the user's cursor
  //   position
  //   - Example: if the formula text is "$c$x + asd|f + 2" where "|" is the user's cursor, then
  //     `formulaSearchQuery` is "asd" because `tokens.leftover` is "asdf + 2", but we only query
  //     the substring of the leftover that precedes the user's cursor
  if (selection != null && selection.start === selection.end) {
    if (getFormulaLength(formulaText) === selection.end) {
      // Even if there are no tokens, set query to '' so we can still show dropdown
      formulaSearchQuery = tokens.leftover ?? '';
    } else if (tokens.leftover != null && tokens.leftover.length > 0) {
      // Look for the last match because `tokens.leftover` ends at the end of the formula text string
      queryStart = formulaText.lastIndexOf(tokens.leftover);
      const {after} = partitionFormulaText(formulaText, selection.start, selection.end);
      queryEnd = formulaText.lastIndexOf(after);
      if (queryStart < queryEnd) {
        formulaSearchQuery = formulaText.substring(queryStart, queryEnd);
      }
    }
  }

  const functionHelp = selection != null ? getScopedFunction(formulaText, selection.end) : null;

  function onChangeFormulaText(
    newFormulaText: string,
    additionalOptions: {
      shouldUpdateEditableDiv: boolean;
      // Cursor position is defined in terms of characters, not tokens (i.e., `$c$x` is length 4)
      newCursorPosition?: number;
    }
  ): void {
    handleChangeFormula({...formula, formulaText: newFormulaText});

    if (additionalOptions.shouldUpdateEditableDiv) {
      // Because the div is uncontrolled, we need to manually set the html when we make an update outside of
      // normal user input
      // Add <br> at the end to ensure the ending newline is rendered correctly
      editableDivRef.current!.innerHTML = `${formulaTextToHTML(newFormulaText, dataFrameSpec)}<br>`;
      const selectionPosition = additionalOptions.newCursorPosition ?? getFormulaLength(newFormulaText);
      selutil.restoreSelection($(editableDivRef.current!)[0], {
        start: selectionPosition,
        end: selectionPosition,
      });
    }
  }

  function completeFormulaText(
    name: string,
    options: {isFunction: boolean; replaceSearchQueryTokens: boolean}
  ): void {
    let prefix: string;
    let suffix = '';

    if (options.replaceSearchQueryTokens) {
      // Replace the search query tokens with the completed function/column
      prefix = tokens.tokens?.map((token) => token.text).join('') ?? '';
      if (queryEnd != null) {
        suffix = formulaText.substring(queryEnd);
      }
    } else if (selection != null) {
      // Insert the new formula text at the location of the user's cursor
      const {before, after} = partitionFormulaText(formulaText, selection.start, selection.end);
      prefix = before;
      suffix = after;
    } else {
      prefix = formulaText;
    }

    let newFormulaTextStart: string;
    if (options.isFunction) {
      newFormulaTextStart = `${prefix}${name}(`;
    } else {
      newFormulaTextStart = `${prefix}"${COLUMN_REFERENCE_PREFIX}${name}" `;
    }
    onChangeFormulaText(`${newFormulaTextStart}${suffix}`, {
      shouldUpdateEditableDiv: true,
      newCursorPosition: getFormulaLength(newFormulaTextStart),
    });
  }

  function getDropdownMenu(): JSX.Element | null {
    // Cases for when to show the two dropdowns:
    //   1. formulaSearchQuery is not null (see initialization of formulaSearchQuery):
    //     a. They have just typed a function (i.e. functionHelp != null):
    //       i. But they haven't typed arguments, e.g. 'abs(' -> help dropdown
    //       ii. They are typing arguments, e.g. 'abs(a)' -> search dropdown
    //     b. They haven't typed a function, e.g. 'a' -> search dropdown
    //   2. formulaSearchQuery is null
    //     a. The cursor is in a function (i.e. functionHelp != null) -> help dropdown
    //     b. The cursor is not in a function (i.e. functionHelp == null) -> no dropdown
    if (functionHelp != null && !formulaSearchQuery) {
      return (
        <FunctionHelpDropdown
          spreadsheetFunction={functionHelp.function as SpreadsheetFunction}
          currentParameterIndex={functionHelp.currentParameterIndex}
        />
      );
    } else if (formulaSearchQuery != null) {
      return (
        <AnalysisFormulaSearchDropdown
          dataFrame={dataFrameSpec}
          onSelectColumn={(columnText) => {
            completeFormulaText(columnText, {isFunction: false, replaceSearchQueryTokens: true});
          }}
          onSelectFunction={(functionName) => {
            completeFormulaText(functionName, {isFunction: true, replaceSearchQueryTokens: true});
          }}
          ref={formulaSearchDropdownRef}
          query={formulaSearchQuery}
        />
      );
    }
    return null;
  }

  function handleKeyDown(e: ElementKeyboardEvent): void {
    // Send keydown events to the formula search dropdown, so that up, down, and enter are handled by the
    // dropdown
    if (formulaSearchDropdownRef.current?.queryKeydownHandler(e)) {
      return;
    }
    if (isEnterKey(e)) {
      e.preventDefault();
      const {start, end} = resolveSelection(formulaText, selection);
      const {before, after} = partitionFormulaText(formulaText, start, end);
      const newFormulaText = `${htmlToFormulaText(before)}\n${htmlToFormulaText(after)}`;
      onChangeFormulaText(newFormulaText, {
        shouldUpdateEditableDiv: true,
        newCursorPosition: getFormulaLength(newFormulaText) - getFormulaLength(after),
      });
    }
  }

  function handlePaste(e: ElementClipboardEvent): void {
    e.preventDefault();
    const parts = wyciwyp.processClipboard(e.clipboardData, {
      parseHTML: false,
    });
    const {start, end} = resolveSelection(formulaText, selection);
    const {before, after} = partitionFormulaText(formulaText, start, end);
    const newFormulaText =
      htmlToFormulaText(before) + parts.map((part) => part.text).join('\n') + htmlToFormulaText(after);
    onChangeFormulaText(newFormulaText, {
      shouldUpdateEditableDiv: true,
      newCursorPosition: getFormulaLength(newFormulaText) - getFormulaLength(after),
    });
  }

  return (
    <div className={css(styles.wrapper)}>
      <FunctionDropdown
        className={css(styles.functionDropdown)}
        isOpen={functionDropdownOpen}
        onFunctionClick={(fn) => {
          completeFormulaText(fn.name, {isFunction: true, replaceSearchQueryTokens: false});
        }}
        primaryFunctions={analysisFunctions}
        setDropdownOpenState={setFunctionDropdownOpen}
        disabled={disabled}
      />
      <AbsoluteDropdownContainer
        canDropUnder={true}
        menu={getDropdownMenu()}
        onRequestClose={() => {
          setCanDisplayEditorDropdown(false);
        }}
        open={canDisplayEditorDropdown && (formulaSearchQuery != null || functionHelp != null)}
      >
        <div
          className={classNames(!disabled && 'editable', css(styles.input))}
          contentEditable={!disabled}
          // oxlint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{__html: formulaTextToHTML(initialInputValue, dataFrameSpec)}}
          onInput={(e) => {
            // When a user types, both onInput and onSelect are fired, so we're setting the selection twice.
            // However, not setting the selection here can cause an existing dropdown to flicker, as it gets
            // unmounted between when the formula text is update here and the onSelect handler is called
            setSelection(selutil.getSelectionIndices());
            onChangeFormulaText(htmlToFormulaText((e.target as HTMLDivElement).innerHTML ?? ''), {
              shouldUpdateEditableDiv: false,
            });
            setCanDisplayEditorDropdown(true);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onSelect={() => {
            setSelection(selutil.getSelectionIndices());
            setCanDisplayEditorDropdown(true);
          }}
          ref={editableDivRef}
        />
      </AbsoluteDropdownContainer>
      <ClickableIcon
        className={css(styles.copyIcon)}
        icon="copy"
        iconStyle={ClickableIconStyle.NEGATIVE}
        onClick={() =>
          copyToClipboard(formulaText, {
            showToastOnSuccess: true,
            toastText: 'Copied formula to clipboard.',
          })
        }
      />
    </div>
  );
}

const HEIGHT = 35;

const styles = StyleSheet.create({
  copyIcon: {
    height: HEIGHT - 16,
    padding: 8,
    ':hover': {
      color: GRAY_DARKER,
    },
  },
  functionDropdown: {
    height: HEIGHT,
    marginRight: '4px',
  },
  input: {
    borderColor: GRAY,
    borderRadius: '4px',
    borderStyle: 'solid',
    borderWidth: 1,
    // Vertical alignment in the div is shifted upward, but we can't use flex as editing with chips doesn't
    // work, so instead add slightly larger top padding
    padding: '6px 10px 4px 10px',
    minHeight: HEIGHT,
    minWidth: 0,
    flexGrow: 1,
    ':focus': {
      borderColor: BLUE_LIGHTER,
    },
    fontFamily: FONT_FAMILY_MONOSPACED,
    fontSize: 16,
  },
  wrapper: {
    display: 'flex',
    marginBottom: 8,
  },
});
